import { ReactNode } from "react";

import type { HeaderContext } from "@tanstack/react-table";
import { ColDef } from "ag-grid-enterprise";
import { isEqual } from "lodash";

import { deepMerge } from "src/utils/object";

export type RenderedData = React.ReactElement | React.ReactNode;
type MakeOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * This interface is a
 * minimum set of metadata
 * and can be extended
 * for different requirements
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface Config<T = any> {
  label?: ReactNode;
  header?: (
    context: HeaderContext<unknown, T>
  ) => string | RenderedData | undefined;
  sortable?: boolean;
  hidden?: boolean;
  size?: number;
  pinned?: "left" | "right";
  rowIdentifier?: ((data: T) => string | undefined) | boolean; // column's datatype value defines primary key. Use boolean for simple value, or when value is an object, use a callback to pick properties for key

  /**
   * Optional alternative flow applied when the evaluated cell data matches against the first of `conditionals` checks
   */
  alternatives?: Array<AlternativeConditional<T>>;

  // extension for adding strongly typed AG Grid column properties
  agGridColumnDefinition?: ColDef;
}

/**
 * A conditional comprised of a logic rule and a renderer. This becomes part of a priority list of alternatives in column config
 */
export interface AlternativeConditional<T> {
  check: (cellValue: Parameters<ColumnConfig<T>["render"]>[0]) => boolean;
  render: ColumnConfig<T>["render"];
  compare?: ColumnConfig<T>["compare"]; // apply a different comparator
}

export interface ColumnConfig<T> {
  render: (val: T) => RenderedData;
  getConfig: () => Config;

  compare?(a: T, b: T): number;
}

// Should require the cell data type for grid rendering
type BaseColumnConfigAgGridConstantsKeys = keyof Pick<ColDef, "cellDataType">;

export const agGridResourcesStringDefault: BaseColumnConfigAgGridConstants = {
  autoHeight: true, // prefer to not be bound to fixed height of a row. Client can override with caution - Long descriptions need consideration
  wrapText: true, // text should never overflow outside the cell. Client can override with caution - Long strings to be truncated or go to tooltip

  cellDataType: "text"
} as const;

export const agGridResourcesNumberDefault: BaseColumnConfigAgGridConstants = {
  cellDataType: "number"
} as const;

export const agGridResourcesBooleanDefault: BaseColumnConfigAgGridConstants = {
  cellDataType: "boolean"
} as const;

export const agGridResourcesDateTimeDefault: BaseColumnConfigAgGridConstants = {
  cellDataType: "dateTime"
} as const;

export const agGridResourcesObjectDefault: BaseColumnConfigAgGridConstants = {
  cellDataType: "object"
} as const;

export interface BaseColumnConfigAgGridConstants
  extends Partial<Omit<ColDef, BaseColumnConfigAgGridConstantsKeys>>,
    Required<Pick<ColDef, BaseColumnConfigAgGridConstantsKeys>> {
  // constants per column renderer definition for AG Grid
}

/**
 * Conditionally apply the optional client AG Grid column definition
 * - "cell type" yields an important hint to AG Grid engine so it does not waste cycles inferring
 */
function applyClientColDefConfig(
  baseAgColDefConfig: Config,
  columnRendererAgGridConstants?: BaseColumnConfigAgGridConstants
): typeof baseAgColDefConfig {
  if (columnRendererAgGridConstants) {
    baseAgColDefConfig.agGridColumnDefinition = deepMerge(
      columnRendererAgGridConstants,
      baseAgColDefConfig.agGridColumnDefinition ?? {}
    );
  }
  return baseAgColDefConfig;
}

export abstract class BaseColumnConfig<T> implements ColumnConfig<T> {
  protected config: Config;

  /**
   * AG Grid will respect column renderer defaults behaviors that influence sort, filtering, editing
   * Subclasses should override with the default AG Grid column renderer constant if not a string
   * @param config - Column configuration object conforming to the `Config` schema
   * @param agGridResourcesDefault - AG grid column def configuration. Defaults to a string datatype for the column
   */
  constructor(
    config: Config,
    agGridResourcesDefault?: MakeOptional<
      BaseColumnConfigAgGridConstants,
      "cellDataType"
    >
  ) {
    applyClientColDefConfig(
      config,
      Object.assign({ ...agGridResourcesStringDefault }, agGridResourcesDefault)
    );

    this.config = config;
  }

  equals(old: T, next: T): boolean {
    /**
     * Lodash equality check is robust for all primitives, structured objects and arrays
     */
    return isEqual(old, next);
  }

  getConfig(): Config {
    return this.config;
  }

  /**
   * Column subclasses may override with a customized identity. The base case for `toString()` can work in most simple cases.
   * Note: the value generated does not have to be unique for all values in the column as that may be impossible to achieve.
   * But, when joined up with other composite keys from other columns, the "joined" string for the entire row must be unique for AG Grid.
   * @param value - The column's intrinsic data representation, but must output a serialized value that, like `valueOf` but more for uniquely identifying the data for grid performance.
   * @return an identity (unique if possible), possibly comprised of various object-level fields. Should value be missing, return undefined.
   */
  identity(value?: T) {
    return value?.toString();
  }

  abstract compare?(a: T, b: T): number;

  abstract render(value: T): RenderedData;
}

export type ExtractType<Type> = Type extends BaseColumnConfig<infer X>
  ? X
  : undefined;
